/**
 * Which interpreter answers for a speaker, and in whose voice.
 *
 * Two resolutions with the same shape and the same failure mode: a fallback
 * that silently produces PLAUSIBLE output. Miss a `clientFor` and that turn
 * quietly runs on the other agent's weights. Miss a `preambleFor` and that turn
 * speaks as the lattice ships while the panel shows the edited text. Neither
 * throws, both read fine, and both invalidate whatever was being measured.
 *
 * WHY THEY ARE HERE rather than next to `Speaker` in debate.ts. That module
 * imports the worker client and the whole converse stack, so it cannot be
 * loaded outside a browser, and a precedence rule nobody can test headlessly is
 * a precedence rule nobody tests. The arguments are structural on purpose:
 * `Speaker` satisfies them without this module knowing that `Speaker` exists.
 */

/** Anything carrying a preamble: a real Speaker, or a fixture. */
export type HasVoice = {
  pack: { manifest: { preamble: string } };
  /** Set to speak as something other than what the lattice ships with. */
  preamble?: string;
};

/**
 * The persona text this speaker actually uses: its override, else its pack.
 *
 * `undefined` means the pack, NOT an empty system prompt. The lab sends
 * undefined when the box is cleared, and if that ever fell through to `""` the
 * speaker would run with no identity at all: a different and much worse
 * experiment than the one someone clearing a field is asking for, and one that
 * would present as a persona that had simply stopped working.
 */
export function preambleFor(self: HasVoice): string {
  return self.preamble ?? self.pack.manifest.preamble;
}

/**
 * Which client answers for this speaker.
 *
 * Generic so this module needs no reference to `MindClient`, which lives behind
 * a Worker constructor and would drag the browser in with it.
 */
export function clientFor<C>(self: { llm?: C }, shared: C): C {
  return self.llm ?? shared;
}
